/**
 * Phase F3 — Provider Distribution Center
 * ----------------------------------------
 * Surfaces Phase 7 distributions for the portfolios the provider is assigned to,
 * along with the provider's own fee amount per distribution.
 *
 * Endpoints used:
 *   GET /api/provider/distribution-center
 *     Returns: { buckets: { generated, approved, ready_for_payout, paid,
 *                            rejected, cancelled, historical },
 *                fee_totals_by_status, portfolios_managed, assignments_count }
 *
 * Pure UI. No backend logic modified.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DollarSign, FileText, CheckCircle, Wallet, Archive,
} from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: 'generated', label: 'Generated', Icon: FileText, color: theme.colors.status.info },
  { key: 'approved', label: 'Approved', Icon: CheckCircle, color: theme.colors.accent.teal },
  { key: 'ready_for_payout', label: 'Ready For Payout', Icon: Wallet, color: theme.colors.status.success },
  { key: 'historical', label: 'Historical', Icon: Archive, color: theme.colors.text.muted },
];

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, boxShadow: theme.colors.shadows.sm,
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

function fmtMoney(n, cur = 'USD') {
  return `${(Number(n) || 0).toFixed(2)} ${cur}`;
}

const StatusPill = ({ status }) => {
  const color =
    status === 'GENERATED' ? theme.colors.status.info :
    status === 'APPROVED' ? theme.colors.accent.teal :
    status === 'READY_FOR_PAYOUT' ? theme.colors.status.success :
    status === 'PAID' ? theme.colors.status.success :
    status === 'REJECTED' ? theme.colors.status.error :
    status === 'CANCELLED' ? theme.colors.text.muted :
    theme.colors.text.muted;
  return (
    <span style={{
      padding: '3px 10px', borderRadius: 999,
      background: `${color}1A`, color,
      fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
    }}>{status || '—'}</span>
  );
};

export default function ProviderDistributionCenter() {
  const [activeTab, setActiveTab] = useState('generated');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/provider/distribution-center`, auth);
        if (cancelled) return;
        setData(res.data || null);
      } catch (e) {
        if (!cancelled) toast.error('Failed to load distribution center');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [auth]);

  const buckets = data?.buckets || {};
  const feeTotals = data?.fee_totals_by_status || {};
  const portfoliosManaged = data?.portfolios_managed || [];

  const totalFees = useMemo(() => {
    let sum = 0;
    Object.values(feeTotals).forEach((v) => { sum += Number(v) || 0; });
    return sum;
  }, [feeTotals]);

  const counts = useMemo(() => {
    const c = {};
    TABS.forEach((t) => { c[t.key] = (buckets[t.key] || []).length; });
    return c;
  }, [buckets]);

  const rows = buckets[activeTab] || [];

  return (
    <div data-testid="provider-distribution-center-page" style={{ padding: 24, maxWidth: 1300, margin: '0 auto' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 800, color: theme.colors.text.primary, margin: 0,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <DollarSign size={26} color={theme.colors.primary} />
          Distribution Center
        </h1>
        <p style={{ color: theme.colors.text.secondary, marginTop: 4 }}>
          Distributions across your managed portfolios with your provider fee per row.
        </p>
      </div>

      {/* Totals card */}
      <div data-testid="provider-fee-totals-card" style={{
        ...cardStyle, padding: 18, marginBottom: 18,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16,
      }}>
        <div>
          <div style={{ fontSize: 12, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Total Fee Across Statuses
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: theme.colors.text.primary, marginTop: 4 }}>
            {fmtMoney(totalFees)}
          </div>
          <div style={{ fontSize: 12, color: theme.colors.text.muted, marginTop: 6 }}>
            {portfoliosManaged.length} portfolio(s) managed · {data?.assignments_count || 0} assignment(s)
          </div>
        </div>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
          {TABS.map((t) => (
            <div key={t.key} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t.label}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: t.color }}>
                {counts[t.key] || 0}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div data-testid="provider-distribution-tabs" style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        {TABS.map((t) => {
          const Icon = t.Icon;
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`provider-dist-tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 16px', borderRadius: theme.radius.cta,
                border: `1px solid ${active ? t.color : theme.colors.borders}`,
                background: active ? `${t.color}15` : theme.colors.bg.card,
                color: active ? t.color : theme.colors.text.secondary,
                cursor: 'pointer', fontWeight: 600, fontSize: 13,
              }}
            >
              <Icon size={16} /> {t.label}
              <span style={{
                marginLeft: 4, background: active ? t.color : theme.colors.bg.tertiary,
                color: active ? '#fff' : theme.colors.text.secondary,
                padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
              }}>{counts[t.key] || 0}</span>
            </button>
          );
        })}
      </div>

      {/* Table */}
      {loading ? (
        <div data-testid="provider-distribution-loading" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.muted }}>
          Loading distributions…
        </div>
      ) : rows.length === 0 ? (
        <div data-testid="provider-distribution-empty" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.secondary }}>
          No distributions in this bucket.
        </div>
      ) : (
        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: theme.colors.bg.secondary }}>
              <tr>
                {['Portfolio', 'Distribution ID', 'Total', 'My Fee', 'Status', 'Date'].map((h) => (
                  <th key={h} style={{
                    textAlign: 'left', padding: '12px 14px',
                    fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
                    color: theme.colors.text.muted, fontWeight: 700,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`provider-dist-row-${r.id}`}
                  style={{ borderTop: `1px solid ${theme.colors.borders}` }}
                >
                  <td style={{ padding: '12px 14px', fontWeight: 600, color: theme.colors.text.primary }}>
                    {r.portfolio_name || r.portfolio_id}
                  </td>
                  <td style={{ padding: '12px 14px', color: theme.colors.text.secondary, fontFamily: 'monospace', fontSize: 12 }}>
                    {(r.id || '').slice(0, 12)}…
                  </td>
                  <td style={{ padding: '12px 14px' }}>{fmtMoney(r.total_amount || r.distribution_amount, r.currency)}</td>
                  <td style={{ padding: '12px 14px', fontWeight: 700, color: theme.colors.primary }}>
                    {fmtMoney(r.my_fee_amount, r.currency)}
                  </td>
                  <td style={{ padding: '12px 14px' }}><StatusPill status={r.status} /></td>
                  <td style={{ padding: '12px 14px', color: theme.colors.text.secondary }}>
                    {fmtDate(r.paid_at || r.approved_at || r.generated_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
